// ─── Grille 2D d'un emplacement (partagée éditeur / visualisation) ───────────
// Rendu pur d'un plan : tuiles (rectangles + zones libres fusionnées), murs
// (fin/épais/porte/fenêtre, contour inclus), pastille de comptage optionnelle.
// Toute la logique de disposition/coordonnées est dans services/plan.js.
import React from 'react';
import { View, Text, TouchableOpacity, Dimensions } from 'react-native';
import { useUI } from '@mop/core';
import { disposerPlan, murEnGrille, plusGrandRect } from '../services/plan';

const BOIS = '#C8873A';   // porte (bois)
const VERRE = '#5AA9E6';  // fenêtre (verre)

export default function PlanGrid({
  parent, cibleIds = [], selId = null, onTapTuile, onLongPressTuile, onTapCase,
  murEdit = false, onTapMur, formeEdit = false, compteDe, onTapPastille, onLongPressPastille, flux = true, maxWidth = 360,
  compact = false,   // fiche article réduite : icônes seules (ni texte ni pastille), surlignage conservé
  cacherIds = [],    // ids de lieux dont la tuile n'est PAS rendue (🌿 masquer les vides) — murs conservés
}) {
  const UI = useUI();
  const cache = cacherIds && cacherIds.length ? new Set(cacherIds) : null;
  const { cols, rows, place } = disposerPlan(parent, flux);
  const larg = Math.min(maxWidth, Dimensions.get('window').width - 48);
  const cs = Math.floor(larg / cols);
  // Écritures des tuiles proportionnelles à la taille d'une case → lisibles même sur un
  // plan réduit (fiche article à 50 %) sans changer le rendu des plans pleine largeur.
  const fEmoji = Math.max(8, Math.min(16, Math.round(cs * 0.42)));
  const fNom = Math.max(6, Math.min(10, Math.round(cs * 0.30)));
  const fEmojiC = Math.max(9, Math.min(15, Math.round(cs * 0.42)));   // icône seule (mode compact) — petite, à distance des murs (bord non tronqué)
  const tuilesInertes = murEdit || formeEdit;
  const murs = murEnGrille(parent);
  const GW = cols * cs, GH = rows * cs;
  const clamp = (v, max) => Math.max(0, Math.min(max, v));

  const renderUnMur = (m, i) => {
    const type = m.type || 'fin';
    const vert = m.o === 'v';
    if (vert ? !(m.x <= cols && m.y < rows) : !(m.y <= rows && m.x < cols)) return null;
    const cx = m.x * cs, cy = m.y * cs, long = cs;
    const barre = (ep, col) => vert
      ? { position: 'absolute', left: cx - ep / 2, top: cy, width: ep, height: long, backgroundColor: col, borderRadius: ep }
      : { position: 'absolute', left: cx, top: cy - ep / 2, width: long, height: ep, backgroundColor: col, borderRadius: ep };
    const epais = m.ep === 'epais';   // épaisseur EXPLICITE (posée sur un mur épais) — pas d'héritage d'un voisin
    if (type === 'porte') {
      const t3 = long / 3, stub = epais ? 7 : 3, leaf = epais ? 9 : 5;
      const seg = (off, len, ep, col) => vert
        ? { position: 'absolute', left: cx - ep / 2, top: cy + off, width: ep, height: len, backgroundColor: col, borderRadius: ep }
        : { position: 'absolute', left: cx + off, top: cy - ep / 2, width: len, height: ep, backgroundColor: col, borderRadius: ep };
      return (
        <React.Fragment key={`m${i}`}>
          <View pointerEvents="none" style={seg(0, t3, stub, UI.ink)} />
          <View pointerEvents="none" style={seg(t3, t3, leaf, BOIS)} />
          <View pointerEvents="none" style={seg(2 * t3, t3, stub, UI.ink)} />
        </React.Fragment>
      );
    }
    if (type === 'fenetre') {
      return (
        <React.Fragment key={`m${i}`}>
          <View pointerEvents="none" style={barre(epais ? 7 : 5, VERRE)} />
          <View pointerEvents="none" style={barre(1, '#EAF4FC')} />
        </React.Fragment>
      );
    }
    return <View key={`m${i}`} pointerEvents="none" style={barre(type === 'epais' ? 7 : 3, UI.ink)} />;
  };

  const pastille = (node, x, y) => {
    if (!compteDe || compact) return null;
    const n = compteDe(node.id);
    if (!n) return null;
    const st = { position: 'absolute', left: x * cs + 4, top: y * cs + 4, minWidth: 18, height: 18, paddingHorizontal: 4, borderRadius: 999, backgroundColor: UI.accent, alignItems: 'center', justifyContent: 'center', zIndex: 5 };
    const txt = <Text style={{ color: '#fff', fontSize: 10, fontWeight: '800' }}>{n}</Text>;
    return ((onTapPastille || onLongPressPastille) && !tuilesInertes)
      ? <TouchableOpacity onPress={() => onTapPastille && onTapPastille(node.id)} onLongPress={() => onLongPressPastille && onLongPressPastille(node.id)} style={st} hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}>{txt}</TouchableOpacity>
      : <View pointerEvents="none" style={st}>{txt}</View>;
  };

  return (
    <View style={{ width: GW, height: GH, alignSelf: 'center', position: 'relative', backgroundColor: UI.champ, borderRadius: 10, borderWidth: 0.5, borderColor: UI.champBord, marginVertical: 10 }}>
      {onTapCase && !murEdit && Array.from({ length: rows }).map((_, y) => Array.from({ length: cols }).map((_, x) => (
        <TouchableOpacity key={`k${x}-${y}`} onPress={() => onTapCase(x, y)} style={{ position: 'absolute', left: x * cs, top: y * cs, width: cs, height: cs, borderWidth: 0.5, borderColor: UI.border }} />
      )))}
      {place.map(p => {
        if (cache && cache.has(p.node.id)) return null;   // 🌿 tuile masquée (lieu vide)
        const cible = cibleIds.includes(p.node.id);
        const sel = selId === p.node.id;
        const aEnfants = (p.node.enfants || []).length > 0;
        const contenu = compact ? (
          <Text style={{ fontSize: fEmojiC }}>{p.node.emoji || '📍'}</Text>
        ) : (
          <>
            <Text style={{ fontSize: fEmoji }}>{p.node.emoji || '📍'}</Text>
            <Text style={{ fontSize: fNom, fontWeight: '700', color: cible ? '#fff' : UI.ink, textAlign: 'center' }} numberOfLines={2}>{p.node.nom}</Text>
            {aEnfants && <Text style={{ fontSize: Math.max(7, fNom - 1), color: cible ? 'rgba(255,255,255,0.8)' : UI.faint }}>▾</Text>}
          </>
        );
        const bg = cible ? UI.accent : (sel ? UI.accentTint : UI.card);
        const bcol = sel ? UI.accent : UI.champBord;
        const bw = sel ? 1.5 : 0.5;
        const freeform = Array.isArray(p.node.cells) && p.node.cells.length > 1;
        if (freeform) {
          const setK = new Set(p.cells.map(c => `${c.x},${c.y}`));
          const inSet = (x, y) => setK.has(`${x},${y}`);
          const rL = plusGrandRect(p.cells);
          const ins = 3;
          return (
            <React.Fragment key={p.node.id}>
              {p.cells.map((c, ci) => {
                const L = inSet(c.x - 1, c.y), R = inSet(c.x + 1, c.y), T = inSet(c.x, c.y - 1), B = inSet(c.x, c.y + 1);
                const rr = 8;
                const st = {
                  position: 'absolute',
                  left: c.x * cs + (L ? 0 : ins), top: c.y * cs + (T ? 0 : ins),
                  width: cs - (L ? 0 : ins) - (R ? 0 : ins), height: cs - (T ? 0 : ins) - (B ? 0 : ins),
                  backgroundColor: bg, borderColor: bcol,
                  borderLeftWidth: L ? 0 : bw, borderRightWidth: R ? 0 : bw, borderTopWidth: T ? 0 : bw, borderBottomWidth: B ? 0 : bw,
                  borderTopLeftRadius: (!L && !T) ? rr : 0, borderTopRightRadius: (!R && !T) ? rr : 0,
                  borderBottomLeftRadius: (!L && !B) ? rr : 0, borderBottomRightRadius: (!R && !B) ? rr : 0,
                };
                return tuilesInertes
                  ? <View key={ci} pointerEvents="none" style={st} />
                  : <TouchableOpacity key={ci} activeOpacity={0.85} onPress={() => onTapTuile && onTapTuile(p.node)} onLongPress={() => onLongPressTuile && onLongPressTuile(p.node)} style={st} />;
              })}
              <View pointerEvents="none" style={{ position: 'absolute', left: rL.x * cs, top: rL.y * cs, width: rL.w * cs, height: rL.h * cs, alignItems: 'center', justifyContent: 'center', padding: 3 }}>{contenu}</View>
              {pastille(p.node, p.x, p.y)}
            </React.Fragment>
          );
        }
        const tuileStyle = { position: 'absolute', left: p.x * cs + 3, top: p.y * cs + 3, width: p.w * cs - 6, height: p.h * cs - 6, borderRadius: 8, backgroundColor: bg, borderWidth: bw, borderColor: bcol, alignItems: 'center', justifyContent: 'center', padding: 3 };
        return (
          <React.Fragment key={p.node.id}>
            {tuilesInertes
              ? <View pointerEvents="none" style={tuileStyle}>{contenu}</View>
              : <TouchableOpacity activeOpacity={0.85} onPress={() => onTapTuile && onTapTuile(p.node)} onLongPress={() => onLongPressTuile && onLongPressTuile(p.node)} style={tuileStyle}>{contenu}</TouchableOpacity>}
            {pastille(p.node, p.x, p.y)}
          </React.Fragment>
        );
      })}
      {murs.map((m, i) => renderUnMur(m, i))}
      {murEdit && onTapMur && (
        <>
          {Array.from({ length: cols + 1 }).map((_, x) => Array.from({ length: rows }).map((_, y) => (
            <TouchableOpacity key={`bv${x}-${y}`} onPress={() => onTapMur('v', x, y)} style={{ position: 'absolute', left: clamp(x * cs - 9, GW - 18), top: y * cs + 5, width: 18, height: cs - 10 }} />
          )))}
          {Array.from({ length: rows + 1 }).map((_, y) => Array.from({ length: cols }).map((_, x) => (
            <TouchableOpacity key={`bh${x}-${y}`} onPress={() => onTapMur('h', x, y)} style={{ position: 'absolute', left: x * cs + 5, top: clamp(y * cs - 9, GH - 18), width: cs - 10, height: 18 }} />
          )))}
        </>
      )}
    </View>
  );
}
